import { useEffect, useMemo, useRef, useState } from "react";
import type { FrameRecords, ReconResult, SideDisplayConfig } from "../types/api";
import { allColumns, applyPins, cellText, frameIndexMap, type VisibleColumn } from "../lib/columns";
import { useColumnFilters } from "../lib/columnFilters";
import { useColumnPins } from "../lib/columnPins";
import { useFrozenOffsets } from "../lib/frozenOffsets";
import { sideSpans } from "../lib/columnGroups";
import { useBandLabels, BAND_LABEL_ATTR } from "../lib/bandLabels";
import { sortPositions, type SortSpec } from "../lib/ledgerView";
import { useGridSelection } from "./useGridSelection";
import { useColumnWidths } from "./useColumnWidths";
import FullscreenTable from "./FullscreenTable";
import { HeadControls } from "./GridControls";
import { SearchPanel, useMultiSearch } from "./SearchPanel";

type Confidence = "weak" | "medium" | "strong";

interface BridgePair {
  cri_index: number;
  insurer_index: number;
  customer: string;
  amount: number;
  date_gap_days: number;
  track: string;
  signature: string;
  confidence?: Confidence;
  amount_residual?: number;
}

// Weakest first — the engine already sorts this way, but the grid must not depend on
// arrival order (a filter or a re-fetch can reorder), so the rank is restated here.
const CONF_RANK: Record<string, number> = { weak: 0, medium: 1, strong: 2 };

const CONF_GLYPH: Record<string, string> = { weak: "▲", medium: "◆", strong: "●" };

const CONF_HELP: Record<string, string> = {
  weak: "Paired on customer + amount only, with a loose amount difference and far apart in time. Check these first.",
  medium: "Amounts agree to the cent, but no shared policy or reference number confirms the pair.",
  strong: "Both sides share a policy/reference number, or the two amounts cancel exactly.",
};

function ConfidencePill({ value }: { value?: string }) {
  if (!value) return null;
  return (
    <span className={`conf-pill conf-pill--${value}`} title={CONF_HELP[value] ?? ""}>
      <span aria-hidden="true">{CONF_GLYPH[value] ?? ""}</span>
      {value}
    </span>
  );
}

// Counts per band, so the reviewer knows how much of the tab actually needs attention
// before scrolling. Bands with no pairs are omitted rather than shown as a zero.
function ConfidenceLegend({ pairs }: { pairs: BridgePair[] }) {
  const counts = useMemo(() => {
    const c: Record<string, number> = { weak: 0, medium: 0, strong: 0 };
    for (const p of pairs) if (p.confidence && p.confidence in c) c[p.confidence] += 1;
    return c;
  }, [pairs]);
  const bands = (["weak", "medium", "strong"] as const).filter((b) => counts[b] > 0);
  if (bands.length === 0) return null;
  return (
    <div className="legend-row">
      {bands.map((b) => (
        <span key={b} className="inline-group">
          <ConfidencePill value={b} />
          <span className="muted legend-note">
            {counts[b].toLocaleString()}
          </span>
        </span>
      ))}
    </div>
  );
}

interface Props {
  result: ReconResult;
  bridgeRejections: string[];
  rebridging: boolean;
  onBridgeReject: (next: string[]) => void;
  insurerName?: string;  // scopes this grid's own (paired) pinned columns
}

// Auto-Matched tab — mirrors app/overrides.py::_render_auto_matched_tab: the name+amount
// bridge auto-matches with a Keep / Remove control. These pairs are ALREADY in the
// totals; Remove adds the pair's signature to bridge_rejections and RE-RECONCILES (the
// bridge runs in the prematch, so this is a full re-run, not the instant finalize path).
//
// Each pair is shown as a FULL row: the entire CRI transaction and the entire Insurer
// transaction it was paired with, side by side (not just customer/amount/date). Every
// column carries an in-header filter (same interaction as the statement grids), and the
// Remove/Restore action is pinned in a sticky column that stays visible on sideways
// scroll (the same .sticky-note-col pattern the notes grid uses).
export default function AutoMatchedTab({ result, bridgeRejections, rebridging, onBridgeReject, insurerName = "" }: Props) {
  const applied = (result.bridge_applied as BridgePair[] | undefined) ?? [];
  const rejected = (result.bridge_rejected as BridgePair[] | undefined) ?? [];

  const cri = result.df_cri as FrameRecords | undefined;
  const insurer = result.df_insurer as FrameRecords | undefined;

  function remove(sig: string) {
    onBridgeReject([...bridgeRejections, sig]);
  }
  function restore(sig: string) {
    onBridgeReject(bridgeRejections.filter((s) => s !== sig));
  }

  return (
    <div>
      <p className="muted">
        <strong>Review these</strong> — they were paired on customer name + amount, weaker evidence
        than a shared policy or reference number, so they're the most likely pairs to be wrong.
        These are already in the totals. Rows are ordered <strong>weakest first</strong>. Each row
        shows the full CRI and Insurer transaction that were paired. Remove any that look wrong —
        the reconciliation re-runs and the totals update.
      </p>

      <ConfidenceLegend pairs={applied} />

      <h4 className="subhead">Applied ({applied.length})</h4>
      {applied.length === 0 ? (
        <p className="muted">No auto-matches applied.</p>
      ) : (
        <PairTable
          pairs={applied} cri={cri} insurer={insurer}
          criConfig={result.display_config?.cri} insConfig={result.display_config?.insurer}
          action="Remove" busy={rebridging} onAction={remove} insurerName={insurerName}
        />
      )}

      {rejected.length > 0 && (
        <>
          <h4 className="subhead--spaced">Removed ({rejected.length})</h4>
          <PairTable
            pairs={rejected} cri={cri} insurer={insurer}
            criConfig={result.display_config?.cri} insConfig={result.display_config?.insurer}
            action="Restore" busy={rebridging} muted onAction={restore} insurerName={insurerName}
          />
        </>
      )}
    </div>
  );
}

// A pair-column carries the visible column plus which side it came from, so the combined
// header can label it "CRI · X" / "Insurer · X" and read the cell from the right row.
interface PairCol extends VisibleColumn {
  side: "cri" | "ins" | "meta";
  compositeKey: string; // unique across both sides (side-prefixed) for filter state
}

// Lead columns (customer, amount, time gap) come from the bridge proposal itself, not
// the frames — so they always lead regardless of per-insurer column naming, and read
// straight off the pair. Keyed under the "meta" side.
const LEAD_COLS: { key: string; label: string; value: (p: BridgePair) => string | number | null }[] = [
  { key: "confidence", label: "Confidence", value: (p) => p.confidence ?? null },
  { key: "customer", label: "Customer", value: (p) => p.customer },
  { key: "amount", label: "Amount", value: (p) => p.amount },
  { key: "amount_residual", label: "Amount Diff", value: (p) => p.amount_residual ?? null },
  { key: "date_gap_days", label: "Time Gap (days)", value: (p) => p.date_gap_days },
];

function PairTable({ pairs, cri, insurer, criConfig, insConfig, action, busy, muted, onAction, insurerName = "" }: {
  insurerName?: string;
  pairs: BridgePair[];
  cri?: FrameRecords;
  insurer?: FrameRecords;
  criConfig?: SideDisplayConfig;
  insConfig?: SideDisplayConfig;
  action: string;
  busy: boolean;
  muted?: boolean;
  onAction: (sig: string) => void;
}) {
  // Build ONE combined frame: each pair is one row whose cells are the paired CRI row's
  // cells followed by the paired insurer row's cells (looked up by original frame index),
  // then the pair signature. Columns are the full populated column set per side (curated
  // columns first — the same allColumns the drill-down tables use), so the WHOLE
  // transaction shows, not a hand-picked subset.
  // Weakest first, then largest: the pair most likely to be wrong AND most expensive
  // to get wrong leads the grid. Stable within a band so a re-render never reshuffles
  // rows under the reviewer's cursor.
  const ordered = useMemo(() => {
    return [...pairs].sort((a, b) => {
      const ra = CONF_RANK[a.confidence ?? ""] ?? 9;
      const rb = CONF_RANK[b.confidence ?? ""] ?? 9;
      if (ra !== rb) return ra - rb;
      return Math.abs(b.amount) - Math.abs(a.amount);
    });
  }, [pairs]);

  const built = useMemo(() => {
    const criCols = cri ? allColumns(cri, criConfig) : [];
    const insCols = insurer ? allColumns(insurer, insConfig) : [];
    const criMap = cri ? frameIndexMap(cri) : new Map<string, number>();
    const insMap = insurer ? frameIndexMap(insurer) : new Map<string, number>();

    // Lead with the pair's customer / amount / time gap, THEN the full CRI columns,
    // THEN the full insurer columns. index is the column's position in the combined frame.
    const pairCols: PairCol[] = [
      ...LEAD_COLS.map((c, i) => ({ key: c.key, label: c.label, index: i, side: "meta" as const, compositeKey: "meta::" + c.key })),
      ...criCols.map((c) => ({ ...c, side: "cri" as const, compositeKey: "cri::" + c.key })),
      ...insCols.map((c) => ({ ...c, side: "ins" as const, compositeKey: "ins::" + c.key })),
    ];

    // Combined frame columns are the composite keys (unique); the pair signature rides in
    // a trailing hidden column so the sticky action knows which pair a row is.
    const columns = [...pairCols.map((c) => c.compositeKey), "_sig"];
    const rows: FrameRecords["rows"] = ordered.map((p) => {
      const cRow = cri?.rows[criMap.get(String(p.cri_index)) ?? -1];
      const iRow = insurer?.rows[insMap.get(String(p.insurer_index)) ?? -1];
      const cells: (string | number | boolean | null)[] = [];
      for (const c of LEAD_COLS) cells.push(c.value(p));
      for (const c of criCols) cells.push(cRow ? cRow[c.index] : null);
      for (const c of insCols) cells.push(iRow ? iRow[c.index] : null);
      cells.push(p.signature);
      return cells;
    });
    const frame: FrameRecords = { columns, rows, indices: ordered.map((_, i) => i), total_rows: ordered.length };
    return { frame, pairCols };
  }, [ordered, cri, insurer, criConfig, insConfig]);

  const { frame, pairCols } = built;
  const sigIdx = frame.columns.indexOf("_sig");
  const confIdx = frame.columns.indexOf("meta::confidence");

  const [openCol, setOpenCol] = useState<string | null>(null);
  // Weakest first until the user sorts (see `ordered`).
  const [sort, setSort] = useState<SortSpec | null>(null);
  const cf = useColumnFilters(frame);

  // The column-filter hook keys distinct-value caches + filter state by column.key. Our
  // frame's column names ARE the composite keys, so drive the hook with a VisibleColumn
  // whose key/index are the composite key and the combined-frame index.
  // Own pin scope ("paired"): this grid's keys are composite (cri::/ins::/meta::), a
  // different vocabulary from the statement tabs, so pins here stay isolated.
  const pins = useColumnPins("paired", insurerName);
  const vis = useMemo<(VisibleColumn & { side: PairCol["side"] })[]>(
    () => applyPins(
      pairCols.map((c, i) => ({ key: c.compositeKey, label: c.label, index: i, side: c.side })),
      pins.pinned,
    ) as (VisibleColumn & { side: PairCol["side"] })[],
    [pairCols, pins.pinned],
  );

  const frozen = useFrozenOffsets(pins.pinned, vis);
  const lastFrozen = useMemo(() => {
    let last: string | undefined;
    for (const c of vis) if (frozen.offsetFor(c.key) !== undefined) last = c.key;
    return last;
  }, [vis, frozen]);

  // Spanning side banner over each band — same device as the Side-by-Side tab and the
  // Excel paired sheets. Derived from the final column order so pins (which can
  // interleave sides) still produce truthful banners.
  const spans = useMemo(() => sideSpans(vis, insurerName), [vis, insurerName]);
  const seamKeys = useMemo(() => {
    const s = new Set<string>();
    spans.forEach((sp, i) => { if (i > 0) s.add(sp.firstKey); });
    return s;
  }, [spans]);
  const bandLabels = useBandLabels(true, spans);
  const bannerRef = useRef<HTMLTableRowElement | null>(null);
  const [groupH, setGroupH] = useState(26);
  useEffect(() => {
    const measure = () => {
      const h = bannerRef.current?.getBoundingClientRect().height;
      if (h) setGroupH((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", measure); };
  }, [spans]);

  const search = useMultiSearch(frame, vis);
  const { setText, setSearchCol, setExact } = search;
  useEffect(() => {
    setText(""); setSearchCol(""); setExact(false); setOpenCol(null); setSort(null);
  }, [frame, setText, setSearchCol, setExact]);

  const filtered = useMemo(() => {
    const keep = cf.rowPredicate(frame.columns);
    const test = search.rowTest;
    const rows: FrameRecords["rows"] = [];
    for (let p = 0; p < frame.rows.length; p++) {
      const r = frame.rows[p];
      if (keep && !keep(r)) continue;
      if (test && !test(p, r)) continue;
      rows.push(r);
    }
    const order = sortPositions(frame, rows, sort);
    return order ? order.map((i) => rows[i]) : rows;
  }, [frame, cf, search.rowTest, sort]);

  // Excel-style cell selection over the DATA columns only — the trailing Action column
  // is excluded (it is a button, not a figure), so a drag across the grid can never
  // swallow a click on it or count it as an ignored text cell.
  //
  // The pair signature is the row id: this grid is unpaginated and re-sorts, so a
  // positional id would let a selection survive onto different pairs.
  const colIdx = useMemo(() => vis.map((c) => c.index), [vis]);
  const colLabels = useMemo(() => vis.map((c) => c.label), [vis]);
  const filteredIds = useMemo(
    () => (sigIdx >= 0 ? filtered.map((r) => String(r[sigIdx])) : filtered.map((_, i) => i)),
    [filtered, sigIdx],
  );
  const sel = useGridSelection(filtered, colIdx, colLabels, filteredIds);
  const cw = useColumnWidths(useMemo(() => [...vis.map((c) => c.key), "__action"], [vis]), frame);

  return (
    <FullscreenTable label="Auto-matched pairs">
      {(scrollMax) => (
    <div className={muted ? "is-muted-block" : undefined}>
      <SearchPanel search={search} matchingRows={filtered.length}
        tools={<>
          {cf.nActive > 0 && (
            <button className="btn btn-sm" onClick={cf.clearAll}>
              Clear {cf.nActive} filter{cf.nActive === 1 ? "" : "s"}
            </button>
          )}
          <span className="muted">{filtered.length.toLocaleString()} of {pairs.length.toLocaleString()} pairs</span>
        </>} />

      <div {...sel.gridProps}>
      <div ref={(el) => { frozen.scrollRef.current = el; sel.scrollHostRef.current = el; bandLabels.register(el); }}
        className="grid-scroll" style={{ maxHeight: scrollMax ?? 480 }}>
        <table className={"data-table data-table--separate data-table--grouped" + cw.className}
          style={{ "--group-h": `${groupH}px`, ...cw.style } as React.CSSProperties}>
          {cw.colgroup}
          <thead>
            <tr ref={bannerRef}>
              {spans.map((sp) => {
                // Freeze the banner with its run when every column in it is frozen,
                // or the label scrolls off the columns it names and clips.
                const left = sp.keys.every((k) => frozen.offsetFor(k) !== undefined)
                  ? frozen.offsetFor(sp.firstKey)
                  : undefined;
                return (
                  <th key={sp.firstKey} colSpan={sp.span} style={{ left }}
                    className={`side-band side-band--${sp.side}`
                      + (left !== undefined ? " side-band--pinned" : "")}>
                    <span className="band-label" {...{ [BAND_LABEL_ATTR]: "" }}
                      data-band-pinned={left !== undefined ? "1" : undefined}>
                      {sp.label}
                    </span>
                  </th>
                );
              })}
              <th className="side-band side-band--meta" />
            </tr>
            <tr>
              {vis.map((c, ci) => {
                const left = frozen.offsetFor(c.key);
                const cls = [
                  left !== undefined ? "pinned-col" + (c.key === lastFrozen ? " pinned-edge" : "") : "",
                  "col-head",
                  sel.isColSelected(ci) ? "col-selected" : "",
                  seamKeys.has(c.key) ? "side-start-ins" : "",
                ].filter(Boolean).join(" ");
                return (
                  <th key={c.key} data-col-key={c.key} className={`nowrap ${cls}`} style={{ left }}>
                    <span className="th-inner">
                      {/* Only the LABEL selects the column — filter and pin controls sit
                          outside it, so opening a filter never foots a column. */}
                      <span className="th-label" title="Click to total this column · Ctrl+click to add another"
                        onClick={(e) => { sel.onHeaderClick(ci, e); sel.focusGrid(); }}>
                        {c.label}
                      </span>
                      <HeadControls frame={frame} col={c}
                        filter={{
                          active: (cf.colFilters[c.key]?.length ?? 0) > 0,
                          open: openCol === c.key,
                          options: () => cf.distinctFor(c),
                          selected: cf.colFilters[c.key] ?? [],
                          onToggleOpen: () => setOpenCol(openCol === c.key ? null : c.key),
                          onChange: (v) => cf.setColFilter(c.key, v),
                        }}
                        sort={{ spec: sort, onSort: setSort }}
                        pin={{ pinned: pins.isPinned(c.key), onToggle: () => pins.toggle(c.key) }} />
                    </span>
                    {cw.grip(c.key)}
                  </th>
                );
              })}
              <th className="sticky-note-col col-action">Action{cw.grip("__action")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, ri) => {
              const sig = sigIdx >= 0 ? String(r[sigIdx]) : String(ri);
              const conf = confIdx >= 0 ? String(r[confIdx] ?? "") : "";
              return (
                <tr key={ri} className={conf === "weak" ? "conf-row--weak" : undefined}>
                  {vis.map((c, ci) => {
                    const v = r[c.index];
                    // Strip the side prefix for date- and money-aware formatting (cellText
                    // keys on the raw column name).
                    const rawKey = c.key.slice(c.key.indexOf("::") + 2);
                    const left = frozen.offsetFor(c.key);
                    const text = cellText(v, rawKey);
                    const cls = [
                      "cell-clip",
                      typeof v === "number" ? "num" : "",
                      left !== undefined ? "pinned-col" : "",
                      left !== undefined && c.key === lastFrozen ? "pinned-edge" : "",
                      c.side === "ins" ? "side-ins" : "",
                      seamKeys.has(c.key) ? "side-start-ins" : "",
                      sel.isSelected(ri, ci) ? "cell-selected" : "",
                    ].filter(Boolean).join(" ");
                    return (
                      <td key={c.key} className={cls} style={{ left }} title={text}
                        data-row={ri} data-col={ci}
                        onMouseDown={(e) => { sel.onCellMouseDown(ri, ci, e); sel.focusGrid(); }}
                        onMouseEnter={() => sel.onCellMouseEnter(ri, ci)}>
                        {c.key === "meta::confidence"
                          ? <ConfidencePill value={v ? String(v) : undefined} />
                          : text}
                      </td>
                    );
                  })}
                  <td className="sticky-note-col cell-center">
                    <button className="btn" disabled={busy} onClick={() => onAction(sig)}>
                      {busy ? "…" : action}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sel.statusBar}
      </div>
    </div>
      )}
    </FullscreenTable>
  );
}
